import React, { useCallback, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import useStore from '../store/useStore';

export default function EnumNode({ id, data }) {
  const updateNodeData = useStore((s) => s.updateNodeData);
  const updateField = useStore((s) => s.updateField);
  const readOnly = useStore((s) => s.readOnly);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectedFieldIndex = useStore((s) => s.selectedFieldIndex);
  const setSelectedNode = useStore((s) => s.setSelectedNode);
  const editingCell = useStore((s) => s.editingCell);
  const setEditingCell = useStore((s) => s.setEditingCell);
  const clearEditing = useStore((s) => s.clearEditing);
  const swapFields = useStore((s) => s.swapFields);

  const dragIndexRef = useRef(null);
  const isSelected = selectedNodeId === id;

  const isEditing = (fieldIndex, part) =>
    editingCell?.nodeId === id && editingCell?.fieldIndex === fieldIndex && editingCell?.part === part;

  const handleFieldClick = useCallback(
    (e, fieldIndex) => {
      e.stopPropagation();
      setSelectedNode(id, fieldIndex);
    },
    [id, setSelectedNode]
  );

  const handleDoubleClick = useCallback(
    (e, fieldIndex, part) => {
      if (readOnly) return;
      e.stopPropagation();
      setEditingCell({ nodeId: id, fieldIndex, part });
    },
    [id, setEditingCell, readOnly]
  );

  // Drag & drop reorder
  const handleDragStart = (e, index) => {
    if (readOnly) { e.preventDefault(); return; }
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
    const fromIndex = dragIndexRef.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      swapFields(id, fromIndex, toIndex);
    }
    dragIndexRef.current = null;
  };

  return (
    <div
      className={`entity-node enum-node ${isSelected ? 'selected' : ''}`}
      onClick={() => setSelectedNode(id)}
    >
      <div
        className="entity-header enum-header"
        onDoubleClick={(e) => handleDoubleClick(e, null, 'tableName')}
      >
        <Handle type="source" position={Position.Left} id="enum-left"
          className="field-handle field-handle-left" style={{ position: 'absolute', left: -6 }} />
        <Handle type="source" position={Position.Right} id="enum-right"
          className="field-handle field-handle-right" style={{ position: 'absolute', right: -6 }} />
        <span className="enum-icon">&#9674;</span>
        {isEditing(null, 'tableName') ? (
          <textarea
            className="entity-name-input nodrag"
            defaultValue={data.name}
            autoFocus
            onBlur={(e) => {
              updateNodeData(id, { name: e.target.value });
              clearEditing();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
            }}
            onFocus={(e) => { e.target.select(); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            onChange={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            spellCheck={false}
            rows={1}
          />
        ) : (
          <div className="entity-name-display">{data.name}</div>
        )}
      </div>
      <div className="entity-fields">
        {data.fields.map((field, index) => {
          const isFieldSelected = isSelected && selectedFieldIndex === index;
          return (
            <div
              key={index}
              className={`entity-field-row ${isFieldSelected ? 'field-selected nodrag' : ''} ${index % 2 === 1 ? 'alt' : ''}`}
              onClick={(e) => handleFieldClick(e, index)}
              draggable={isFieldSelected}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDoubleClick={(e) => handleDoubleClick(e, index, 'name')}
            >
              <div className="field-name-cell enum-field-name">
                {isEditing(index, 'name') ? (
                  <textarea
                    className="field-name-input nodrag"
                    defaultValue={field.name}
                    autoFocus
                    onBlur={(e) => {
                      updateField(id, index, { name: e.target.value.toUpperCase() });
                      clearEditing();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                    }}
                    onFocus={(e) => { e.target.select(); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    onChange={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    placeholder="VALUE"
                    spellCheck={false}
                    rows={1}
                  />
                ) : (
                  <span className="field-name-display">
                    {field.name || <em className="placeholder-text">VALUE</em>}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
